import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { useCallback, useState } from 'react';

/** Animation states for the fox avatar */
export type FoxyState = 'idle' | 'thinking' | 'celebrating' | 'peeking' | 'winking';

interface FoxyAvatarProps {
  readonly state: FoxyState;
  readonly size?: number;
  readonly className?: string;
}

/**
 * Animation file path per state.
 * Note: .lottie files need to be sourced from LottieFiles marketplace
 * or custom-created. These are placeholder paths — place actual .lottie
 * assets in public/animations/ before use.
 */
const STATE_ANIMATION_MAP: Record<FoxyState, string> = {
  idle: '/animations/foxy-idle.lottie',
  thinking: '/animations/foxy-thinking.lottie',
  celebrating: '/animations/foxy-celebrating.lottie',
  peeking: '/animations/foxy-peeking.lottie',
  winking: '/animations/foxy-winking.lottie',
};

/** States that loop continuously */
const LOOPING_STATES: ReadonlySet<FoxyState> = new Set(['idle', 'thinking']);

/**
 * Animated fox avatar component using dotlottie-react (WebAssembly renderer).
 *
 * - `idle` and `thinking` loop continuously
 * - `celebrating`, `peeking`, `winking` play once
 * - Falls back to a static fox emoji if the .lottie file fails to load
 */
export function FoxyAvatar({ state, size = 64, className = '' }: FoxyAvatarProps) {
  const [loadError, setLoadError] = useState(false);

  const src = STATE_ANIMATION_MAP[state];
  const loop = LOOPING_STATES.has(state);

  const dotLottieRefCallback = useCallback((instance: DotLottie | null) => {
    if (!instance) return;
    instance.addEventListener('loadError', () => setLoadError(true));
  }, []);

  if (loadError) {
    return (
      <div
        className={`flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
        role="img"
        aria-label="Foxy assistant"
      >
        <span style={{ fontSize: size * 0.7 }}>{'🦊'}</span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <DotLottieReact
        src={src}
        loop={loop}
        autoplay
        style={{ width: size, height: size }}
        dotLottieRefCallback={dotLottieRefCallback}
      />
    </div>
  );
}
